import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Chip, Grid } from "@mui/material";
import { HealthAndSafety, MoneyOff, WorkOff } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import { MainLayout } from "@shared/ui/MainLayout";
import { DataGridTable } from "@shared/ui/DataGridTable";
import { ErrorServer } from "@shared/ui/alerts";
import { CustomToolbarReloadComponent } from "@shared/ui/MuiTable/CustomToolbarReloadComponent";
import MDBox from "@vendor/components/MDBox";
import MenuCard from "@vendor/examples/Cards/MenuCards";

import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import type { TranslationKey } from "@shared/i18n/translationKey";

import { CustomToolbar } from "../components/CustomToolbar";
import { useLicenseApplications } from "../hooks/useLicenseApplications";
import { useVacationDaysAvailable } from "../hooks/useVacationDaysAvailable";
import { DEFAULT_VACATION_DAYS, STATUS_CHIP_META } from "../constants";

export const ManagementRequestPage = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const navigate = useNavigate();

  const {
    data,
    isFetching: isFetchingList,
    isError: isListError,
    refetch,
  } = useLicenseApplications();
  const { data: dayAvailability = DEFAULT_VACATION_DAYS, isLoading: isLoadingDays } =
    useVacationDaysAvailable();

  useEffect(() => {
    if (isListError) ErrorServer().then();
  }, [isListError]);

  const onClickAction = () => navigate("/solicitudes/registro");

  const status = (value: string) => {
    const meta = STATUS_CHIP_META[value];
    if (!meta) return null;
    const sx = meta.color ? { backgroundColor: meta.color, color: "#fff" } : undefined;
    return <Chip label={t(meta.labelKey)} sx={sx} />;
  };

  const columns: GridColDef[] = [
    { field: "supervisorEmail", headerName: t("supervisorEmail"), flex: 1 },
    {
      field: "type",
      headerName: t("type"),
      flex: 1,
      // El tipo llega del CRM y se usa como clave ("Otro", "Permiso NO Remunerado"...); no
      // es un conjunto cerrado, y si la clave no existe i18next devuelve el valor tal cual.
      valueFormatter: (value: string) => t(value as TranslationKey),
    },
    { field: "comments", headerName: t("observations"), flex: 1 },
    { field: "status", headerName: t("status"), flex: 1, renderCell: (params: GridRenderCellParams) => status(params.value) },
    { field: "startDate", headerName: t("startDate"), flex: 1 },
    { field: "endDate", headerName: t("endDate"), flex: 1 },
  ];

  const toolbarActions = (
    <>
      <CustomToolbarReloadComponent title={t("reloadData")} onClickAction={refetch} />
      <CustomToolbar title={t("registerApplication")} onClickAction={onClickAction} t={t} />
    </>
  );

  return (
    <MainLayout loading={isFetchingList || isLoadingDays}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6, lg: 4, xl: 4 }}>
          <MDBox mb={1.5}>
            <MenuCard
              icon={WorkOff}
              title={t("daysAvailability") + ": " + dayAvailability.daysAvailability}
            />
          </MDBox>
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 4, xl: 4 }}>
          <MDBox mb={1.5}>
            <MenuCard
              icon={MoneyOff}
              title={t("daysAvailabilityPaid") + ": " + dayAvailability.daysAvailabilityPaid}
            />
          </MDBox>
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 4, xl: 4 }}>
          <MDBox mb={1.5}>
            <MenuCard
              icon={HealthAndSafety}
              title={t("daysSicknessRecord") + ": " + dayAvailability.daysSicknessRecord}
            />
          </MDBox>
        </Grid>
      </Grid>
      <DataGridTable
        columns={columns}
        rows={data}
        title={t("application")}
        language={language}
        height={600}
        toolbarActions={toolbarActions}
      />
    </MainLayout>
  );
};
